import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, Zap, Shield, Crown, X, ArrowRight } from 'lucide-react';

const PricingModal = ({ 
  isOpen, 
  onClose, 
  user, 
  hasActive799Plan, 
  onSelectPlan, 
  onRequireLogin,
  hasActiveAnalysis = false
}) => {
  const [mobileSelectedPlan, setMobileSelectedPlan] = useState('premium_799');

  // Prevent background scrolling while modal is open on mobile and desktop
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChoosePlan = (planType) => {
    if (!user) {
      onClose();
      onRequireLogin();
      return;
    }

    if (planType === 'single_49' && !hasActiveAnalysis) {
      alert("To pay per single resume, please upload your resume in the 'Tailor Resume' tab first. Or you can purchase the 20-Resume Premium Monthly Plan right now!");
      return;
    }

    onClose();
    onSelectPlan(planType);
  };

  const renderSingleResumeCard = (isMobile = false) => (
    <div className={`rounded-2xl bg-slate-950/70 border border-slate-800 p-4 sm:p-6 flex flex-col justify-between hover:border-slate-700 transition relative ${isMobile ? 'shadow-lg' : ''}`}>
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-bold text-slate-100 flex items-center gap-2">
            <Zap className="w-4 h-4 text-indigo-400" />
            Pay Per Resume
          </h3>
          <span className="px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-bold bg-slate-800 text-indigo-300 border border-indigo-500/30">
            67% OFF
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1">Ideal for a single targeted job application</p>

        {/* Price Display */}
        <div className="mt-4 sm:mt-5 pb-4 sm:pb-5 border-b border-slate-800">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-black text-white">₹49</span>
            <span className="text-base sm:text-lg text-slate-400 line-through">₹149</span>
            <span className="text-xs text-slate-400">/ resume</span>
          </div>
          <p className="text-xs text-emerald-400 font-semibold mt-1">Save ₹100 on every resume</p>
        </div>

        {/* Features */}
        <ul className="mt-4 sm:mt-5 space-y-2.5 sm:space-y-3 text-xs text-slate-300">
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span><strong>1 ATS-Optimized Resume</strong> tailored to your job description</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span><strong>90+ Target ATS Score</strong> guaranteed with keyword matching</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span><strong>3 High-Conversion Layouts</strong> (Professional, Standard, Clean)</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span><strong>Permanent Storage</strong> in your "My Resumes" dashboard</span>
          </li>
        </ul>
      </div>

      <div className="mt-6 sm:mt-8">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleChoosePlan('single_49')}
          className="w-full py-3 px-4 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold text-xs sm:text-sm transition border border-slate-700 flex items-center justify-center gap-2 cursor-pointer shadow-md"
        >
          <span>Pay Per Resume (₹49)</span>
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </motion.button>
      </div>
    </div>
  );

  const renderGoldPlanCard = (isMobile = false) => (
    <div className={`rounded-2xl bg-gradient-to-b from-indigo-950/70 to-slate-950/90 border-2 border-indigo-500/70 p-4 sm:p-6 flex flex-col justify-between relative shadow-xl shadow-indigo-950/50 ${isMobile ? 'pt-6' : ''}`}>
      {/* Highlight ribbon */}
      <div className="absolute -top-3 right-4 sm:right-6 bg-gradient-to-r from-amber-500 to-indigo-500 text-slate-950 font-black text-[9px] sm:text-[10px] tracking-wider uppercase px-2.5 sm:px-3 py-1 rounded-full shadow-md">
        MOST POPULAR • 65% OFF
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-bold text-amber-300 flex items-center gap-2">
            <Crown className="w-4 h-4 text-amber-400" />
            Premium Monthly (Gold Plan)
          </h3>
        </div>
        <p className="text-xs text-indigo-200/80 mt-1">Best value for active job seekers & multi-role applications</p>

        {/* Price Display */}
        <div className="mt-4 sm:mt-5 pb-4 sm:pb-5 border-b border-indigo-800/40">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl sm:text-4xl font-black text-white">₹799</span>
            <span className="text-base sm:text-lg text-indigo-300/60 line-through">₹2,299</span>
            <span className="text-xs text-indigo-300">/ month</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-amber-300 font-semibold">Just ₹39.95 per resume</span>
            <span className="text-xs text-slate-400">• Save ₹1,500</span>
          </div>
        </div>

        {/* Features */}
        <ul className="mt-4 sm:mt-5 space-y-2.5 sm:space-y-3 text-xs text-slate-200">
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-amber-300">20 Tailored Resumes</strong> per month (Full Quota)</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span><strong>1-Click Quota PDF Generation</strong> — No repeated payments</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span><strong>Unlimited Layout Switching</strong> &amp; Live Real-time Previews</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span><strong>30 Days Full Validity</strong> with active quota</span>
          </li>
          <li className="flex items-start gap-2.5">
            <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span><strong>24/7 Priority Support</strong> via WhatsApp &amp; Email</span>
          </li>
        </ul>
      </div>

      <div className="mt-6 sm:mt-8">
        <motion.button
          whileHover={{ scale: 1.02, boxShadow: '0 10px 25px -4px rgba(99, 102, 241, 0.5)' }}
          whileTap={{ scale: 0.98 }}
          onClick={() => handleChoosePlan('premium_799')}
          className="w-full py-3.5 px-4 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white rounded-xl font-bold text-xs sm:text-sm transition shadow-lg flex items-center justify-center gap-2 cursor-pointer"
        >
          <Crown className="w-4 h-4 text-amber-300" />
          <span>Get Premium Monthly for ₹799</span>
        </motion.button>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md overflow-y-auto p-2.5 sm:p-6 flex flex-col items-center justify-start sm:justify-center"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-4xl bg-slate-900 border border-slate-700/80 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-8 my-3 sm:my-8 shrink-0"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-5 sm:right-5 text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800 transition cursor-pointer z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Modal Header */}
          <div className="text-center max-w-xl mx-auto space-y-1.5 sm:space-y-2 mb-4 sm:mb-8 pt-1 sm:pt-0 pr-8 sm:pr-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold bg-amber-500/10 text-amber-300 border border-amber-500/25">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <span>Limited Time Launch Discounts</span>
            </div>
            <h2 className="text-lg sm:text-3xl font-extrabold text-white tracking-tight leading-snug">
              Choose the Perfect Plan for Your Career
            </h2>
            <p className="text-xs sm:text-sm text-slate-400">
              Get fact-locked, high-scoring ATS resumes tailored to any job description. Instant downloads in 3 premium layouts.
            </p>
          </div>

          {/* Mobile Plan Selector Tabs (< md) */}
          <div className="md:hidden flex items-center p-1 bg-slate-950/80 rounded-xl border border-slate-800 mb-4">
            <button
              type="button"
              onClick={() => setMobileSelectedPlan('premium_799')}
              className={`flex-1 py-2 px-2.5 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                mobileSelectedPlan === 'premium_799'
                  ? 'bg-gradient-to-r from-amber-500/20 to-indigo-600/30 text-amber-300 border border-amber-500/40 shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Crown className="w-3.5 h-3.5 text-amber-400" />
              <span>Gold Plan (₹799)</span>
            </button>
            <button
              type="button"
              onClick={() => setMobileSelectedPlan('single_49')}
              className={`flex-1 py-2 px-2.5 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 ${
                mobileSelectedPlan === 'single_49'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Zap className="w-3.5 h-3.5 text-indigo-300" />
              <span>Single (₹49)</span>
            </button>
          </div>

          {/* Mobile View: Active Selected Plan */}
          <div className="md:hidden">
            {mobileSelectedPlan === 'premium_799' ? renderGoldPlanCard(true) : renderSingleResumeCard(true)}
            <div className="text-center mt-3">
              <button
                type="button"
                onClick={() => setMobileSelectedPlan(prev => prev === 'premium_799' ? 'single_49' : 'premium_799')}
                className="text-xs text-indigo-400 hover:underline"
              >
                {mobileSelectedPlan === 'premium_799' ? 'Looking for a single resume instead? Tap to view ₹49 plan' : 'Want 20 resumes with 65% discount? Tap to view Gold Plan'}
              </button>
            </div>
          </div>

          {/* Desktop View: Side-by-Side Grid (md:) */}
          <div className="hidden md:grid md:grid-cols-2 gap-6 items-stretch">
            {renderSingleResumeCard(false)}
            {renderGoldPlanCard(false)}
          </div>

          {/* Footer note */}
          <div className="mt-5 sm:mt-6 pt-3 sm:pt-4 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-500 gap-2 text-center sm:text-left">
            <div className="flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span>100% Secure Payments via Razorpay (UPI, Cards, NetBanking)</span>
            </div>
            <span>Need help? WhatsApp: +918877966766</span>
          </div>

        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PricingModal;
